/**
 * rolePageCopyData.js — render fields for every FusionMSI role page.
 * Shaped by prompt 38; MKT-01, MKT-20, MKT-23, MKT-25.
 * Invariants: published is false for every row; Holds text from
 * docs/marketing/ROLE_PAGE_COPY.md never appears here; a role page never
 * grants access. Patient stays a redirect (MKT-08).
 * Related: rolePageAccess.js, RolePage.jsx, ROLE_SPLASH_PAGE_REGISTER.md.
 */

/** @typedef {'public' | 'unlisted' | 'signInOnly'} RoleVisibility */
/** @typedef {'contact' | 'signIn' | 'findCare' | 'createMasterChart' | 'scroll' | 'role' | 'none'} CtaAction */

/**
 * @typedef {object} RoleCta
 * @property {string} label
 * @property {CtaAction} action
 * @property {string} [topic] contact topic when it is not the page slug
 * @property {string} [target] scroll target id
 * @property {string} [slug] role slug for action "role"
 */

export const ROLE_PAGE_PREVIEW_BANNER = 'Preview — not published';

/** Hub copy (register row 1). Rendered only by the flagged hub in prompt 39. */
export const ROLE_HUB_COPY = {
  registerRow: 1,
  slug: null,
  visibility: 'public',
  published: false,
  headline: 'Clinical AI that your clinicians improve, and health records your patients control.',
  subhead:
    'FusionMSI is a network of hospitals, clinics, and training programs that share one platform, and each institution\'s clinicians shape how its AI behaves.',
  primary: { label: 'Find your role', action: 'scroll', target: 'role-cards' },
  roleCards: [
    { slug: 'patient', title: 'Patients', blurb: 'Find care and carry your record with you.' },
    { slug: 'clinician', title: 'Clinicians', blurb: 'Verified clinical answers and documentation help at the bedside.' },
    {
      slug: 'healthcare-institution',
      title: 'Healthcare institutions',
      blurb: 'Bring FusionMSI to your hospital or clinic.',
    },
    { slug: 'fusion-founder', title: 'Lead your region', blurb: 'Become a Fusion Founder.' },
    {
      slug: 'morningreport-licensee',
      title: 'Schools and training programs',
      blurb: 'Teach with MorningReport Cases.',
    },
    { slug: 'third-party-advertiser', title: 'Advertisers', blurb: 'Reach patients looking for care.' },
  ],
  signInLink: { label: 'Already part of an institution? Sign in.', action: 'signIn' },
  sections: [
    {
      title: 'How the network fits together',
      numbered: true,
      items: [
        { body: 'Patients own access to their MasterChart record, which travels between care sites.' },
        { body: 'Institutions run FusionMSI\'s clinical applications for their own patients.' },
        {
          body: 'Clinicians review the AI\'s work, and their reviews shape how their institution\'s AI behaves.',
        },
        { body: 'Fusion Founders lead the network in each region.' },
      ],
    },
  ],
  footer:
    'FusionMSI does not practice medicine; clinical decisions remain with clinicians.',
  footerLinks: ['business-model', 'historical-precedent', 'about', 'terms', 'privacy'],
};

export const ROLE_PAGES = [
  {
    registerRow: 2,
    slug: 'patient',
    kind: 'redirect',
    visibility: 'public',
    published: false,
    headline: 'Find care nearby, and bring your medical history with you.',
    subhead:
      'CareMap shows care sites near you. MasterChart keeps your conditions, allergies, medications, and surgeries in one record that you control.',
    primary: { label: 'Find care near you', action: 'findCare' },
    sections: [
      {
        title: 'What you can do',
        items: [
          { title: 'Find care.', body: 'See hospitals, clinics, and urgent care sites on CareMap.' },
          {
            title: 'Keep one record.',
            body: 'MasterChart holds your health history and goes with you between care sites in the network.',
          },
          {
            title: 'Decide who sees it.',
            body: 'You control who can access your record, and you can take it with you.',
          },
          {
            title: 'Check symptoms before you go.',
            body: 'CareStart asks about your symptoms and helps you choose where to go.',
          },
        ],
      },
      {
        title: 'Your privacy',
        items: [
          {
            body: 'Your record is yours to control. Your access never depends on whether a care site stays in the network.',
          },
          {
            body: 'We use your information for research or AI training only if you agree, through our own consent process. Each choice is optional and does not affect your care.',
          },
        ],
      },
    ],
    closing: {
      line: 'Your health history should not start over at every new clinic.',
      primary: { label: 'Create your MasterChart', action: 'createMasterChart' },
    },
    footer: 'FusionMSI does not give medical advice. In an emergency, call your local emergency number.',
  },
  {
    registerRow: 3,
    slug: 'clinician',
    visibility: 'public',
    published: false,
    headline: 'Clinical AI that shows its sources, and gets better because you correct it.',
    subhead:
      'FusionMSI gives you voice-first clinical reference at the bedside, documentation help, and your patients\' portable history, all inside your institution\'s own governed AI.',
    primary: { label: 'Sign in', action: 'signIn' },
    sections: [
      {
        title: 'What you can use',
        items: [
          {
            title: 'DecisionPoint (ED, invite-only preview).',
            body: 'Ask out loud at the bedside. Answers come from physician-authored records, read verbatim, never improvised. Doses and numbers stay on screen.',
          },
          {
            title: 'ChartMind.',
            body: 'AI help drafting encounter documentation, governed by your institution\'s own rules.',
          },
          {
            title: 'MasterChart.',
            body: 'Your patient\'s conditions, allergies, medications, and surgical history, when they choose to share them.',
          },
          {
            title: 'VoxPont.',
            body: 'Two-way spoken interpretation between you and your patient.',
          },
        ],
      },
      {
        title: 'Why it\'s different',
        items: [
          {
            title: 'Retrieve, never generate, for bedside answers.',
            body: 'DecisionPoint content passes two physician reviews before it is treated as verified; until then it is marked unverified.',
          },
          {
            title: 'Your corrections shape your AI.',
            body: 'When clinicians at your institution review the AI\'s work, those improvements are applied to your institution\'s AI.',
          },
          {
            title: 'You stay in charge.',
            body: 'Every clinical decision is yours. The AI supports; it does not decide.',
          },
        ],
      },
    ],
    decisionPoint: {
      title: 'DecisionPoint',
      id: 'decisionpoint',
      intro:
        'Emergency medicine · Invite-only preview. Ask out loud at the bedside. FusionMSI retrieves physician-authored text and does not invent doses or differentials. Numbers stay on screen.',
      items: [
        {
          id: 'how-it-works',
          title: 'Voice-first at the bedside.',
          body: 'Start a session and speak naturally. Full detail, doses, and intervals stay on screen.',
        },
        {
          title: 'Retrieve, never generate.',
          body: 'Bedside answers come from stored clinical records. Content passes two physician reviews before it is treated as verified. Until then it is marked unverified.',
        },
        {
          title: 'Structured for EM thinking.',
          body: 'Records are organized as presentations, linked diagnoses, and decision rules, with can\'t-miss lists, workup, and disposition.',
        },
        {
          id: 'capabilities',
          title: 'Capabilities.',
          body: 'Bedside voice retrieval, a draft clinical library, and authoring tools for review and access. This preview is invite-only.',
        },
        {
          id: 'access',
          title: 'Access and preview limits.',
          body: 'Sign in with an invite from your operator. There is no open signup. Voice navigation in this preview is English-only, and the tool is for licensed clinicians, not patients.',
        },
      ],
    },
    closing: {
      line: 'Already at a participating institution? Sign in to get started.',
      primary: { label: 'Sign in', action: 'signIn' },
      secondary: {
        label: 'Not yet participating? Share FusionMSI with your leadership.',
        action: 'contact',
      },
    },
    footer: 'FusionMSI does not practice medicine. Clinical judgment remains with the treating clinician.',
  },
  {
    registerRow: 4,
    slug: 'healthcare-institution',
    visibility: 'public',
    published: false,
    headline: 'Your institution\'s AI should learn from your clinicians.',
    subhead:
      'FusionMSI gives your care sites clinical AI, patient-controlled records, and a governance model that puts your clinicians\' expertise into how your AI behaves.',
    primary: { label: 'Start a conversation', action: 'contact' },
    sections: [
      {
        title: 'What changes for your institution',
        items: [
          {
            title: 'Patients arrive with their history.',
            body: 'MasterChart travels with patients between care sites, so fewer visits start from zero.',
          },
          {
            title: 'AI you govern.',
            body: 'Your institution sets rules for how the AI behaves, on top of MD3C\'s global and regional layers.',
          },
          {
            title: 'Clinicians improve it.',
            body: 'Annotators review real AI interactions; TCI Managers promote the improvements. Their work shapes how the AI behaves at your institution.',
          },
          {
            title: 'Throughput tools.',
            body: 'VisitStart for arrival and SavoirCare boards for ED and clinic flow.',
          },
        ],
      },
      {
        title: 'How it works',
        numbered: true,
        items: [
          {
            title: 'One invitation.',
            body: 'Your leader sets up the institution and invites the leadership team.',
          },
          {
            title: 'Leadership invites departments.',
            body: 'Each department assigns its AI-quality roles.',
          },
          {
            title: 'Go live, governed.',
            body: 'Clinicians use the applications; their reviews improve the AI from the first week.',
          },
        ],
      },
      {
        title: 'How to join',
        items: [
          {
            body: 'Each institution signs its own agreement directly with MD3C, often introduced by the Fusion Founder for its region.',
          },
        ],
      },
    ],
    closing: {
      line: 'See what FusionMSI looks like at your institution.',
      primary: { label: 'Start a conversation', action: 'contact' },
    },
    footer: 'This page is a general description, not an offer. FusionMSI does not practice medicine.',
  },
  {
    registerRow: 5,
    slug: 'it-operations',
    visibility: 'public',
    published: false,
    headline: 'What your IT team needs to know before saying yes.',
    subhead:
      'FusionMSI is a hosted service. There is nothing to install on a server, and your clinicians use it in a browser on the devices they already have.',
    primary: { label: 'Request the security summary', action: 'contact', topic: 'security-summary' },
    sections: [
      {
        title: 'Security commitments',
        items: [
          { body: 'Encryption in transit and at rest for patient data MD3C hosts.' },
          { body: 'Staff access to that data is restricted and protected by multi-factor authentication.' },
          { body: 'Audit logging of access.' },
          { body: 'Written security obligations on our hosting subprocessors.' },
          { body: 'Incident notice within 72 hours of discovery, in both directions.' },
          { body: 'An annual written security summary, on request.' },
        ],
      },
      {
        title: 'Operations',
        items: [
          {
            title: 'Availability.',
            body: 'We use commercially reasonable efforts to keep the service available, give advance notice of maintenance outside peak clinical hours where practicable, and report material outages. Keep downtime procedures in place.',
          },
          {
            title: 'Roles and access.',
            body: 'Your institution invites its own users and assigns roles. Every role is checked on every sign-in.',
          },
          {
            title: 'Your data.',
            body: 'Your institution owns the record content its clinicians create. Patients control access to their own MasterChart.',
          },
          {
            title: 'Local law.',
            body: 'Each Country Addendum sets in-country data duties, such as local copies and regulator notices.',
          },
        ],
      },
    ],
    closing: {
      primary: { label: 'Request the security summary', action: 'contact', topic: 'security-summary' },
      secondary: { label: 'Start a conversation', action: 'contact' },
    },
    footer:
      'Commitments described here are set out in full in the network terms and your Country Addendum, which control.',
  },
  {
    registerRow: 6,
    slug: 'fusion-founder',
    visibility: 'public',
    published: false,
    eyebrow: 'Fusion Founder · One institution per country or region',
    headline: 'Bring FusionMSI to your country, and lead the network that grows there.',
    subhead:
      'A Fusion Founder is the anchor institution for its region. You use FusionMSI\'s clinical AI and patient-controlled records for your own patients, and you lead the recruitment of the hospitals and clinics around you.',
    primary: { label: 'Start a conversation', action: 'contact' },
    sections: [
      {
        title: 'What a Fusion Founder does',
        intro: 'A Fusion Founder works in two capacities at once.',
        items: [
          {
            title: 'Cares for its own patients with FusionMSI.',
            body: 'Your institution holds its own license and uses the clinical applications, such as ChartMind, VisitStart, and SavoirCare, alongside your patients\' own MasterChart records.',
          },
          {
            title: 'Recruits the institutions around it.',
            body: 'You introduce hospitals, clinics, and training programs in your territory to the network. Each signs its own agreement directly with MD3C.',
          },
          {
            title: 'Shapes the regional AI layer.',
            body: 'As Regional Admin, you adapt FusionMSI\'s AI instructions to your region\'s practice and language. MD3C reviews these changes for patient safety.',
          },
          {
            title: 'Anchors the territory\'s CareMap advertising.',
            body: 'Your designated territory is your Ad Territory on CareMap, the map patients use to find care.',
          },
        ],
      },
      {
        title: 'What you get',
        items: [
          {
            title: 'First position in your region.',
            body: 'Founder territories never overlap, and no other Fusion Founder earns credit for advertising in yours.',
          },
          {
            title: 'Fees that fall as your network grows.',
            body: 'As institutions in your territory pay for FusionMSI, a credit is applied against your own institution\'s usage fees.',
          },
          {
            title: 'You set your recruits\' pricing.',
            body: 'Within the network\'s minimum rates, you decide what the institutions you recruit pay.',
          },
          {
            title: 'A protected head start.',
            body: 'When MD3C names an institution it wants in your territory, you get three months of exclusive time to sign it. You can also register institutions you are already courting.',
          },
          {
            title: 'A regional voice in the AI.',
            body: 'Your clinicians shape how FusionMSI\'s AI behaves for your region.',
          },
          {
            title: 'A stable relationship.',
            body: 'Five-year terms that renew automatically. MD3C may decline renewal only for an uncured performance shortfall measured against targets you agree at the start.',
          },
          {
            note: 'No figure on this page is a promise or estimate of income or savings. Your terms are set in your own agreement.',
          },
        ],
      },
      {
        title: 'How it works',
        numbered: true,
        items: [
          { title: 'Start a conversation.', body: 'Tell us about your institution and the region you serve.' },
          {
            title: 'Review the terms with your own counsel.',
            body: 'You receive the network terms that apply to every Founder, plus a worksheet of the terms that are negotiated: fees, territory, and targets.',
          },
          { title: 'Sign.', body: 'Your agreement, your country\'s addendum, and the network terms together form the contract.' },
          { title: 'Go live at your institution.', body: 'Fees begin only once usage metering is live for your institution.' },
          {
            title: 'Build your network.',
            body: 'Recruit hospitals, clinics, and training programs. Each signs directly with MD3C, and its attribution to you is tracked in a ledger you can see at any time.',
          },
          { title: 'Renew every five years.', body: 'Flat-dollar terms can be revisited at each renewal.' },
        ],
      },
      {
        title: 'What we ask of you',
        intro: 'These terms are the same for every Fusion Founder. We state them up front so there are no surprises.',
        items: [
          {
            title: 'Meet the targets you agree.',
            body: 'Each Founder agrees an onboarding pace and territory growth targets. Missing them, after a six-month cure period, can reduce your territory or end renewal.',
          },
          {
            title: 'Recruiting credit, not a sales monopoly.',
            body: 'MD3C may also sign institutions and sell advertising in your territory directly.',
          },
          {
            title: 'Show the FusionMSI logo.',
            body: 'Display it prominently in your patient-facing marketing, and place MD3C\'s door signage where the applications are used.',
          },
          {
            title: 'Stay with MasterChart.',
            body: 'While you are a Founder, and for three years after, you may not adopt a competing cross-institution patient-record product in your territory. Systems your government requires are always allowed.',
          },
          {
            title: 'Keep clinical responsibility.',
            body: 'Your clinicians make every clinical decision. MD3C does not practice medicine and may pause any regional AI change it believes is unsafe.',
          },
          {
            title: 'Report incidents within 72 hours.',
            body: 'Patient-safety and data-breach incidents, in both directions.',
          },
          {
            title: 'Respect patient control.',
            body: 'A patient\'s access to their own MasterChart record never depends on your institution\'s status in the network.',
          },
          {
            body: 'If your agreement ends, your recruits keep their own agreements with MD3C, and responsibility for them returns to MD3C.',
          },
        ],
      },
    ],
    closing: {
      headline: 'Is your institution the right anchor for your region?',
      line: 'We designate one Fusion Founder per territory. If you lead an established institution and want to build what comes next for the care sites around you, let\'s talk.',
      primary: { label: 'Start a conversation', action: 'contact' },
    },
    footer:
      'This page is a general description, not an offer. The terms of any relationship are set only in a signed agreement, which we encourage you to review with independent counsel. FusionMSI does not practice medicine; clinical decisions remain with your clinicians.',
  },
  {
    registerRow: 7,
    slug: 'fusion-partner',
    visibility: 'public',
    published: false,
    eyebrow: 'Fusion Partner · Your own license, your own patients',
    headline: 'Run FusionMSI at your institution, on your own agreement.',
    subhead:
      'A Fusion Partner licenses FusionMSI directly from MD3C and uses it for its own patients, with its clinicians shaping how the AI works locally.',
    primary: { label: 'Start a conversation', action: 'contact' },
    sections: [
      {
        title: 'What a Fusion Partner does',
        items: [
          {
            title: 'Uses the clinical applications',
            body: 'for its own patients: documentation, arrival, throughput, and interpretation.',
          },
          { title: 'Connects patients to MasterChart,', body: 'so their history follows them.' },
          { title: 'Improves its own AI.', body: 'Your clinicians\' reviews make the AI better at your institution.' },
        ],
      },
      {
        title: 'What you get',
        items: [
          { title: 'A direct agreement with MD3C.', body: 'No middleman holds your license.' },
          { title: 'Pay for what you use.', body: 'Fees are charged per patient per day of use, not per seat.' },
          {
            title: 'A credit tied to your patients\' MasterChart.',
            body: 'When patients who use MasterChart at your institution upgrade, part of that revenue is credited against your fees.',
          },
          { title: 'Local control.', body: 'With LocalPrompt, your institution can add its own rules to the AI.' },
          { title: 'A stable relationship.', body: 'Five-year terms that renew automatically.' },
          { note: 'Nothing on this page is a promise or estimate of savings. Your terms are set in your own agreement.' },
        ],
      },
      {
        title: 'What we ask of you',
        items: [
          {
            body: 'Show the FusionMSI logo in your patient-facing marketing, and place MD3C\'s door signage where the applications are used.',
          },
          {
            body: 'Stay with MasterChart for cross-institution records, during the agreement and for three years after, within your institution. Government-required systems are always allowed.',
          },
          { body: 'Keep clinical responsibility; MD3C may pause a local AI change it believes is unsafe.' },
          { body: 'Report patient-safety and data incidents within 72 hours.' },
        ],
      },
    ],
    closing: { primary: { label: 'Start a conversation', action: 'contact' } },
    footer:
      'This page is a general description, not an offer. Terms are set only in a signed agreement; review it with independent counsel. FusionMSI does not practice medicine.',
  },
  {
    registerRow: 8,
    slug: 'fusion-affiliate',
    visibility: 'public',
    published: false,
    eyebrow: 'Fusion Affiliate (Introductory Member)',
    headline: 'Help patients find you on CareMap, without a software commitment.',
    subhead:
      'A Fusion Affiliate, also called an Introductory Member, is a care site that advertises on CareMap without licensing FusionMSI\'s clinical applications. It is the simplest way to join the network.',
    primary: { label: 'Start a conversation', action: 'contact' },
    sections: [
      {
        title: 'What you get',
        items: [
          {
            title: 'Visibility where patients look.',
            body: 'Carousel Ads on CareMap, the map patients use to find care.',
          },
          {
            title: 'A place in the network.',
            body: 'Join now, and move up to a full Fusion Partner license whenever you\'re ready.',
          },
          {
            title: 'Training content, if you want it.',
            body: 'You may also license MorningReport Cases for your clinicians\' education.',
          },
        ],
      },
      {
        title: 'What we ask of you',
        items: [
          { body: 'Keep your ad content truthful and lawful in your country; you are responsible for it.' },
          { body: 'Maintain your Introductory Member status to keep advertising.' },
        ],
      },
    ],
    closing: { primary: { label: 'Start a conversation', action: 'contact' } },
    footer:
      'This page is a general description, not an offer. Advertising on CareMap is launching. In the United States, placements are by ZIP code. Outside the United States, placements are by local market areas, set per country.',
  },
  {
    registerRow: 9,
    slug: 'morningreport-licensee',
    visibility: 'public',
    published: false,
    headline: 'Teaching cases your learners can practice on, every morning.',
    subhead:
      'MorningReport Cases gives your faculty and learners simulated clinical cases for teaching. Your school licenses it, and faculty use is free.',
    primary: { label: 'Start a conversation', action: 'contact' },
    sections: [
      {
        title: 'What you get',
        items: [
          {
            title: 'Simulated cases, ready to teach.',
            body: 'Learners work through cases built for education, never real patient data.',
          },
          { title: 'Faculty tools.', body: 'Faculty author, assign, and review cases at no charge.' },
          {
            title: 'Pay only for learning.',
            body: 'Your institution is charged each time a learner starts a case. Faculty use is not charged.',
          },
          { title: 'No clinical license needed.', body: 'You license MorningReport Cases on its own.' },
        ],
      },
      {
        title: 'What we ask of you',
        items: [
          { body: 'Designate your faculty accurately in the product; MD3C may audit designations.' },
          { body: 'Never enter real patient information. MorningReport Cases is not for patient care.' },
          { body: 'Your curriculum, assessment, and accreditation decisions remain yours.' },
        ],
      },
    ],
    closing: { primary: { label: 'Start a conversation', action: 'contact' } },
    footer:
      'MorningReport Cases is an educational tool. Its content is not medical advice, and use does not by itself satisfy any accreditation or continuing-education requirement.',
  },
  {
    registerRow: 10,
    slug: 'collection-remittance-agent',
    visibility: 'unlisted',
    published: false,
    headline: 'Collecting payments for the network in your country.',
    subhead:
      'Where paying MD3C directly is impractical, MD3C may designate its Fusion Founder to collect payments locally and remit them to MD3C. This page explains what that role involves.',
    primary: { label: 'Reply to your MD3C contact', action: 'none' },
    sections: [
      {
        title: 'What the role involves',
        items: [
          {
            title: 'A dedicated account.',
            body: 'You open an account, such as a mobile-money paybill or a bank account, used only for payments owed to MD3C.',
          },
          {
            title: 'Funds held in trust.',
            body: 'Money in that account belongs to MD3C. You never deposit your own funds there or deduct anything from it.',
          },
          {
            title: 'Monthly remittance.',
            body: 'You send the collected funds to MD3C, itemized, by the 10th of each month.',
          },
          {
            title: 'Audit and handover.',
            body: 'MD3C may audit the account, and you hand it over if the designation ends.',
          },
          {
            title: 'Collateral.',
            body: 'MD3C may ask for a bank guarantee covering two to three months of expected collections.',
          },
        ],
      },
      {
        title: 'Why it matters',
        items: [
          {
            body: 'A patient or institution that pays into your Collection Account has paid MD3C. That makes local payment simple for everyone in your territory.',
          },
        ],
      },
    ],
    footer:
      'Designation is made by MD3C in writing, at its sole discretion, and may be revoked. The designation letter and network terms control.',
  },
  {
    registerRow: 11,
    slug: 'institution-admin',
    visibility: 'public',
    published: false,
    headline: 'You set up FusionMSI for your institution.',
    subhead:
      'The Institution Admin receives the first invitation, sets up the institution, and invites the leadership team. From there, access cascades to every department.',
    primary: { label: 'Sign in', action: 'signIn' },
    sections: [
      {
        title: 'What you do',
        items: [
          { title: 'Set up your institution.', body: 'Name, sites, and departments.' },
          { title: 'Invite your leadership.', body: 'Each leader then invites their own teams.' },
          { title: 'Assign the AI-quality roles.', body: 'Name TCI Managers for each clinical area.' },
          { title: 'Appoint specialist roles,', body: 'such as a Marketing Executive for CareMap advertising.' },
          {
            title: 'Set your institution\'s AI rules.',
            body: 'Your rules apply on top of MD3C\'s global and regional layers.',
          },
        ],
      },
      {
        title: 'How to get this role',
        items: [
          {
            body: 'This role is given by invitation from MD3C or your region\'s Fusion Founder when your institution joins.',
          },
        ],
      },
    ],
    closing: {
      primary: { label: 'Sign in', action: 'signIn' },
      secondaryLine: 'Expecting an invitation and haven\'t received it? Contact the person at your institution who signed the FusionMSI agreement.',
    },
    footer: '',
  },
  {
    registerRow: 12,
    slug: 'facility-staff',
    visibility: 'public',
    published: false,
    headline: 'See who\'s waiting, who\'s next, and what\'s done.',
    subhead: 'Facility staff run the day with FusionMSI\'s status boards, scheduling, and arrival tools.',
    primary: { label: 'Sign in', action: 'signIn' },
    sections: [
      {
        title: 'What you use',
        items: [
          {
            title: 'SavoirCare boards',
            body: 'for the ED and outpatient clinic: patients, rooms, and who is seeing whom.',
          },
          {
            title: 'VisitStart',
            body: 'at arrival: patients check in on-site by QR code, link, or kiosk, or staff help them.',
          },
          { title: 'Scheduling and clinic messaging', body: 'for the team.' },
        ],
      },
      {
        title: 'How to get this role',
        items: [{ body: 'Ask your institution\'s administrator for an invitation.' }],
      },
    ],
    closing: {
      primary: { label: 'Sign in', action: 'signIn' },
      secondaryLine: 'No invitation yet? Ask your institution\'s FusionMSI administrator.',
    },
    footer: '',
  },
  {
    registerRow: 13,
    slug: 'regional-admin',
    visibility: 'public',
    published: false,
    headline: 'Make FusionMSI\'s AI fit your region\'s practice.',
    subhead:
      'The Regional Admin sets the regional layer of the AI\'s instructions, the default every institution in the territory inherits. This role sits within the region\'s Fusion Founder.',
    primary: { label: 'Sign in', action: 'signIn' },
    sections: [
      {
        title: 'What you do',
        items: [
          { title: 'Adapt the AI', body: 'to regional guidelines, common conditions, medicines, and language.' },
          {
            title: 'Set the default',
            body: 'for every institution in your territory; each can add its own local rules on top.',
          },
          {
            title: 'Work with MD3C\'s clinical review.',
            body: 'MD3C may review any regional change and pause one it believes is unsafe.',
          },
        ],
      },
      {
        title: 'What we ask of you',
        items: [
          {
            body: 'Your institution is responsible for the effects of its regional changes, including at institutions that inherit them.',
          },
          { body: 'Attest periodically that no one has paid you, undisclosed, to influence a prompt.' },
        ],
      },
      {
        title: 'How to get this role',
        items: [
          {
            body: 'Assigned within your region\'s Fusion Founder. Ask your Founder\'s FusionMSI administrator.',
          },
        ],
      },
    ],
    closing: { primary: { label: 'Sign in', action: 'signIn' } },
    footer: '',
  },
  {
    registerRow: 14,
    slug: 'tci-manager',
    visibility: 'public',
    published: false,
    headline: 'You decide which improvements reach the bedside.',
    subhead:
      'A TCI Manager is the clinical steward for one area of care, such as triage. Your annotators flag what the AI should do better; you decide what becomes the new standard.',
    primary: { label: 'Sign in', action: 'signIn' },
    sections: [
      {
        title: 'What you do',
        items: [
          {
            title: 'Review proposed improvements',
            body: 'that come from your annotators\' work, and approve, return, or reject each one.',
          },
          { title: 'Lead your annotators.', body: 'See their work, coach them, and message them directly.' },
          { title: 'Watch loop health.', body: 'See whether review is keeping pace with use.' },
          { title: 'Choose your clinic\'s tools.', body: 'Approve which clinical interactions your clinic uses.' },
        ],
      },
      {
        title: 'Why it matters',
        items: [{ body: 'Your area\'s expertise becomes part of how its AI works.' }],
      },
      {
        title: 'How to get this role',
        items: [{ body: 'Assigned by your Institution Admin. Ask your institution\'s FusionMSI administrator.' }],
      },
    ],
    closing: { primary: { label: 'Sign in', action: 'signIn' } },
    footer: '',
  },
  {
    registerRow: 15,
    slug: 'clinical-annotator',
    visibility: 'public',
    published: false,
    headline: 'Teach the AI what a good clinician would do.',
    subhead:
      'Clinical Annotators review real AI interactions in their area, such as triage, and mark what should change. Your notes become proposed improvements for your TCI Manager to approve.',
    primary: { label: 'Sign in', action: 'signIn' },
    sections: [
      {
        title: 'What you do',
        items: [
          { title: 'Replay AI sessions', body: 'in the clinical area you are assigned to.' },
          { title: 'Annotate', body: 'what the AI did well and what it should do differently.' },
          { title: 'See your effect', body: 'as your notes turn into improvements.' },
        ],
      },
      {
        title: 'Why it matters',
        items: [
          {
            body: 'You are the reason your institution\'s AI reflects how your clinicians practice, not a vendor\'s generic model.',
          },
        ],
      },
      {
        title: 'How to get this role',
        items: [
          {
            body: 'Assigned for each clinical area by your TCI Manager or Institution Admin. You can hold more than one assignment.',
          },
        ],
      },
    ],
    closing: { primary: { label: 'Sign in', action: 'signIn' } },
    footer: '',
  },
  {
    registerRow: 16,
    slug: 'technical-readiness-lead',
    visibility: 'public',
    published: false,
    headline: 'Know when each area is ready to go live.',
    subhead:
      'The Technical Readiness Lead watches readiness across the institution: which clinical areas are set up, staffed, and integrated. This is a technical role, not a clinical one.',
    primary: { label: 'Sign in', action: 'signIn' },
    sections: [
      {
        title: 'What you do',
        items: [
          { title: 'Track readiness', body: 'across every clinical area on one dashboard.' },
          { title: 'Flag gaps', body: 'such as missing role assignments or incomplete setup.' },
          { title: 'Coordinate integration', body: 'with your IT team.' },
          { note: 'You see readiness; you do not annotate or approve clinical changes.' },
        ],
      },
      {
        title: 'How to get this role',
        items: [{ body: 'Assigned by your Institution Admin. Ask your institution\'s FusionMSI administrator.' }],
      },
    ],
    closing: { primary: { label: 'Sign in', action: 'signIn' } },
    footer: '',
  },
  {
    registerRow: 17,
    slug: 'global-tci-manager',
    visibility: 'public',
    published: false,
    headline: 'See what\'s working across every institution.',
    subhead:
      'The Global TCI Manager reviews AI improvements network-wide, and decides which ones are worth sharing beyond the institution that made them.',
    primary: { label: 'Sign in', action: 'signIn' },
    sections: [
      {
        title: 'What you do',
        items: [
          { title: 'Review improvements across institutions', body: 'for each clinical area.' },
          { title: 'Share what works.', body: 'Help strong improvements reach other institutions.' },
          { title: 'Watch network-wide loop health.', body: '' },
        ],
      },
      {
        title: 'How to get this role',
        items: [{ body: 'Appointed by MD3C.' }],
      },
    ],
    closing: { primary: { label: 'Sign in', action: 'signIn' } },
    footer: '',
  },
  {
    registerRow: 18,
    slug: 'marketing-executive',
    visibility: 'public',
    published: false,
    headline: 'Put your care site in front of patients searching nearby.',
    subhead:
      'The Marketing Executive runs your institution\'s advertising on CareMap. It is a marketing-only role, with no access to clinical records or AI settings.',
    primary: { label: 'Sign in', action: 'signIn' },
    sections: [
      {
        title: 'What you do',
        items: [
          {
            title: 'Advertising is launching.',
            body: 'In the United States, placements are by ZIP code. Outside the United States, placements are by local market areas, set per country.',
          },
          { title: 'Details and availability vary by market', body: 'as advertising launches.' },
          { title: 'Track performance', body: 'of your placements.' },
        ],
      },
      {
        title: 'How to get this role',
        items: [{ body: 'Appointed by your Institution Admin. Ask your institution\'s FusionMSI administrator.' }],
      },
    ],
    closing: { primary: { label: 'Sign in', action: 'signIn' } },
    footer: 'Your institution is responsible for the truthfulness and legality of its ad content.',
  },
  {
    registerRow: 19,
    slug: 'third-party-advertiser',
    visibility: 'public',
    published: false,
    headline: 'Reach people at the moment they are looking for care.',
    subhead:
      'FusionMSI\'s CareMap helps patients find care sites. Brands can register interest in advertising to that audience, sold directly by MD3C.',
    primary: { label: 'Register interest', action: 'contact' },
    sections: [
      {
        title: 'What to expect',
        items: [
          { title: 'Sold by MD3C.', body: 'Placements and pricing are set by MD3C.' },
          { title: 'Regional availability varies.', body: 'Some regions do not carry third-party advertising.' },
          {
            title: 'Health-safe standards.',
            body: 'All ads must comply with the advertising and healthcare-marketing laws of each country where they run.',
          },
          { title: 'Privacy first.', body: 'Ads are never targeted using a patient\'s medical record.' },
        ],
      },
    ],
    closing: { primary: { label: 'Register interest', action: 'contact' } },
    footer:
      'Registering interest is not an order or a commitment by either party. Advertising is subject to MD3C\'s approval and a separate written agreement.',
  },
  {
    registerRow: 20,
    slug: 'local-safety-monitor',
    visibility: 'public',
    published: false,
    headline: 'A second set of eyes on local AI changes.',
    subhead:
      'MD3C may designate a region\'s Fusion Founder as its Local Safety Monitor. The monitor reviews the local AI changes made by institutions in its territory and reports patient-safety concerns to MD3C.',
    primary: { label: 'Sign in', action: 'signIn' },
    sections: [
      {
        title: 'What the role involves',
        items: [
          { title: 'Read-only review', body: 'of local AI instructions in the territory.' },
          {
            title: 'Report concerns to MD3C.',
            body: 'The monitor recommends; only MD3C can pause a change.',
          },
          {
            title: 'Strict confidentiality.',
            body: 'What the monitor sees is MD3C confidential information and is used only for safety review.',
          },
        ],
      },
      {
        title: 'What it does not do',
        items: [
          { body: 'It does not make the Founder responsible for another institution\'s changes.' },
          { body: 'It does not create any duty to patients or third parties.' },
        ],
      },
      {
        title: 'How to get this role',
        items: [{ body: 'Designated by MD3C in writing; MD3C may revoke it at any time.' }],
      },
    ],
    closing: { primary: { label: 'Sign in', action: 'signIn' } },
    footer: '',
  },
  {
    registerRow: 21,
    slug: 'md3c-platform-admin',
    visibility: 'signInOnly',
    published: false,
    staffAudience: 'platformAdmin',
    signedOut: {
      headline: 'MD3C staff sign-in',
      primary: { label: 'Sign in', action: 'signIn' },
    },
    headline: 'Platform administration',
    sections: [
      {
        title: 'What this role covers',
        items: [
          { body: 'The global layer of the AI\'s instructions and model settings.' },
          { body: 'Inviting new institutions and setting their partner tier.' },
          { body: 'Platform-wide settings, kill switches, and readiness review.' },
        ],
      },
      {
        title: 'Reminders',
        items: [
          { body: 'Changes here affect every institution. Follow the change process.' },
          { body: 'Never share credentials; access is personal and audited.' },
        ],
      },
    ],
    footer: '',
  },
  {
    registerRow: 22,
    slug: 'md3c-finance-team',
    visibility: 'signInOnly',
    published: false,
    /**
     * MD3C finance roles are not built (BM-1). Until they exist, only a
     * platform admin (admin claim) may see this page. D-38-03.
     */
    staffAudience: 'platformAdmin',
    signedOut: {
      headline: 'MD3C staff sign-in',
      primary: { label: 'Sign in', action: 'signIn' },
    },
    headline: 'Network finance',
    sections: [
      {
        title: 'What this role covers',
        items: [
          { body: 'Monthly invoices for every institution in the network.' },
          {
            body: 'Posting ledger entries. Only this team posts financial entries; mistakes are fixed with a new correcting entry, never by editing.',
          },
          { body: 'Resolving invoice disputes (raised within 15 days) and ledger disputes (within 180 days).' },
          { body: 'Collection-agent remittances and statements.' },
        ],
      },
    ],
    footer: '',
  },
  {
    registerRow: 23,
    slug: 'account-admin',
    visibility: 'public',
    published: false,
    headline: 'See every charge, and question any of them.',
    subhead: 'The Account Admin manages your institution\'s FusionMSI billing: your ledger, your invoices, and any disputes.',
    primary: { label: 'Sign in', action: 'signIn' },
    sections: [
      {
        title: 'What you do',
        items: [
          { title: 'View your ledger at any time.', body: 'Only your institution and MD3C can see it.' },
          { title: 'Dispute an invoice', body: 'within 15 days, and any other ledger item within 180 days.' },
          {
            title: 'For Fusion Founders:',
            body: 'register institutions you are courting, and see the billings behind your territory credit.',
          },
        ],
      },
      {
        title: 'Worth knowing',
        items: [
          { body: 'Only MD3C posts financial entries. Errors are corrected with a new entry, never by editing an old one.' },
          { body: 'No ledger contains patient information.' },
        ],
      },
      {
        title: 'How to get this role',
        items: [{ body: 'Appointed by your Institution Admin.' }],
      },
    ],
    closing: { primary: { label: 'Sign in', action: 'signIn' } },
    footer: '',
  },
  {
    registerRow: 24,
    slug: 'finance-viewer',
    visibility: 'public',
    published: false,
    headline: 'Read-only access to your institution\'s FusionMSI billing.',
    subhead:
      'Finance Viewers can see the ledger and invoices at any time, for reconciliation and audit, without changing anything.',
    primary: { label: 'Sign in', action: 'signIn' },
    sections: [
      {
        title: 'What you can see',
        items: [
          { body: 'Your institution\'s ledger and monthly invoices.' },
          { body: 'Fees, credits, and corrections, each as its own entry.' },
          { note: 'To dispute a charge, ask your Account Admin.' },
        ],
      },
      {
        title: 'How to get this role',
        items: [{ body: 'Appointed by your Institution Admin.' }],
      },
    ],
    closing: { primary: { label: 'Sign in', action: 'signIn' } },
    footer: '',
  },
  {
    registerRow: 25,
    slug: 'morningreport-faculty',
    visibility: 'public',
    published: false,
    headline: 'Write the case once. Teach it to every learner.',
    subhead:
      'MorningReport Cases lets faculty author, assign, and review simulated teaching cases. Faculty use is free to your institution.',
    primary: { label: 'Sign in', action: 'signIn' },
    sections: [
      {
        title: 'What you do',
        items: [
          { title: 'Author cases', body: 'for your learners, or use the shared library.' },
          { title: 'Assign cases', body: 'to a group or an individual.' },
          { title: 'Review learners\' work', body: 'and give feedback.' },
        ],
      },
      {
        title: 'Worth knowing',
        items: [
          { body: 'Cases are simulated. Never use real patient information.' },
          {
            body: 'Your institution designates its faculty in the product. If you open a case to complete it for your own learning, that use counts as a learner\'s.',
          },
        ],
      },
      {
        title: 'How to get this role',
        items: [{ body: 'Designated by your institution. Ask your program\'s MorningReport administrator.' }],
      },
    ],
    closing: {
      primary: { label: 'Sign in', action: 'signIn' },
      secondary: {
        label: 'Your school isn\'t licensed yet?',
        action: 'role',
        slug: 'morningreport-licensee',
      },
    },
    footer: 'MorningReport Cases is an educational tool and does not provide medical advice.',
  },
  {
    registerRow: 26,
    slug: 'morningreport-learner',
    visibility: 'public',
    published: false,
    headline: 'Work up a case before rounds, the way you will on shift.',
    subhead:
      'MorningReport Cases gives you simulated patients to work through, from history to diagnosis, with feedback along the way.',
    primary: { label: 'Sign in', action: 'signIn' },
    sections: [
      {
        title: 'What you do',
        items: [
          { title: 'Open a case', body: 'your faculty assigned, or choose one to practice.' },
          { title: 'Work it up', body: 'step by step.' },
          { title: 'Learn from feedback', body: 'on your reasoning.' },
        ],
      },
      {
        title: 'How to get access',
        items: [
          {
            body: 'Through your school or training program. Ask your program director whether your institution licenses MorningReport Cases.',
          },
        ],
      },
    ],
    closing: { primary: { label: 'Sign in', action: 'signIn' } },
    footer:
      'MorningReport Cases is an educational tool. Its content is not medical advice and is not a substitute for supervised clinical training.',
  },
  {
    registerRow: 27,
    slug: 'department-prompt-editor',
    visibility: 'public',
    published: false,
    headline: 'Tune the AI for your department, not just your hospital.',
    subhead:
      'Department and unit editors add rules for their own area, such as a pediatric ED or a fast track, on top of the institution\'s settings.',
    primary: { label: 'Sign in', action: 'signIn' },
    sections: [
      {
        title: 'What you do',
        items: [
          { title: 'Add rules for your area', body: 'that apply only to your department or unit.' },
          { title: 'See where each rule comes from:', body: 'global, regional, institution, or yours.' },
        ],
      },
      {
        title: 'Worth knowing',
        items: [
          {
            body: 'Your institution is responsible for the changes its editors make. MD3C may review any change for patient safety and pause one it believes is unsafe.',
          },
        ],
      },
      {
        title: 'How to get this role',
        items: [{ body: 'Appointed within your institution. Ask your institution\'s FusionMSI administrator.' }],
      },
    ],
    closing: { primary: { label: 'Sign in', action: 'signIn' } },
    footer: '',
  },
  {
    registerRow: 28,
    slug: 'specialty-knowledge-reviewer',
    visibility: 'public',
    published: false,
    headline: 'Your specialty, checked by someone who practices it.',
    subhead:
      'Specialty Knowledge Reviewers are invited physicians who proofread the clinical knowledge behind FusionMSI in their own field.',
    primary: { label: 'Sign in', action: 'signIn' },
    sections: [
      {
        title: 'What you do',
        items: [
          {
            title: 'Review clinical content',
            body: 'for your specialty: presentations, diagnoses, and how they connect.',
          },
          { title: 'Correct and approve', body: 'what is accurate, and flag what is not.' },
        ],
      },
      {
        title: 'Why it matters',
        items: [{ body: 'FusionMSI\'s clinical knowledge is only as good as the specialists who check it.' }],
      },
      {
        title: 'How to get this role',
        items: [{ body: 'By invitation from MD3C.' }],
      },
    ],
    closing: { primary: { label: 'Sign in', action: 'signIn' } },
    footer: '',
  },
  {
    registerRow: 29,
    slug: 'pageguide-content-editor',
    visibility: 'public',
    published: false,
    headline: 'Make FusionMSI\'s spoken help sound like your clinic.',
    subhead:
      'PageGuide gives a short spoken introduction to each screen, played only when someone taps it. Content editors adjust that wording for their clinic.',
    primary: { label: 'Sign in', action: 'signIn' },
    sections: [
      {
        title: 'What you do',
        items: [
          { title: 'Edit introductions', body: 'for the screens your clinic uses.' },
          { title: 'Turn PageGuide off', body: 'for a page where it isn\'t helpful.' },
        ],
      },
      {
        title: 'How to get this role',
        items: [{ body: 'Appointed within your institution. Ask your institution\'s FusionMSI administrator.' }],
      },
    ],
    closing: { primary: { label: 'Sign in', action: 'signIn' } },
    footer: '',
  },
  {
    registerRow: 30,
    slug: 'parent-guardian',
    visibility: 'public',
    published: false,
    headline: 'Your child\'s health record, with you in control.',
    subhead:
      'For a patient under 18, a parent or guardian gives consent where the law requires it. Here is what that means.',
    primary: { label: 'Find care near you', action: 'findCare' },
    sections: [
      {
        title: 'What you decide',
        items: [
          {
            title: 'Consent to care-site processing,',
            body: 'where your country\'s law requires a parent\'s or guardian\'s consent for a minor.',
          },
          {
            title: 'Optional uses of data,',
            body: 'such as research or AI training, each asked separately and never required for care.',
          },
          { title: 'Changing your mind', body: 'later, for future use.' },
        ],
      },
      {
        title: 'How it works',
        items: [
          {
            body: 'The care site treating your child records your consent. You can ask them to show you what you agreed to.',
          },
        ],
      },
    ],
    closing: {
      primary: { label: 'Find care near you', action: 'findCare' },
      secondaryLine: 'Questions? Ask the care site treating your child.',
    },
    footer: 'FusionMSI does not give medical advice. In an emergency, call your local emergency number.',
  },
  {
    registerRow: 31,
    slug: 'btyb-marketer',
    visibility: 'public',
    published: false,
    headline: 'Put your name on your city\'s CareStart page.',
    subhead:
      'Each city\'s CareStart page carries one line: "Brought to you by." Sponsors set a price per thousand views and a budget; the highest offer that can still pay for the next view is shown.',
    primary: { label: 'Register interest', action: 'contact' },
    sections: [
      {
        title: 'How it works',
        numbered: true,
        items: [
          { title: 'Pick a city.', body: '' },
          { title: 'Write a short message', body: 'to appear with your name.' },
          { title: 'Set your price and budget.', body: 'Your name shows while your budget covers views.' },
          { note: 'Until a sponsor is showing, the line reads "FusionMSI".' },
        ],
      },
    ],
    closing: { primary: { label: 'Register interest', action: 'contact' } },
    footer: 'Registering interest is not an order. Sponsorship is subject to MD3C\'s approval and a separate agreement.',
  },
];

export function getRolePage(slug) {
  if (!slug) return null;
  return ROLE_PAGES.find((page) => page.slug === slug) || null;
}

/** Pages a /for/:slug route may render. Patient stays a redirect. */
export function getRoutableRolePage(slug) {
  const page = getRolePage(slug);
  if (!page || page.kind === 'redirect') return null;
  return page;
}

export function listRoutableRoleSlugs() {
  return ROLE_PAGES.filter((page) => page.kind !== 'redirect').map((page) => page.slug);
}
